import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

export const Contact = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: 'general',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    toast.success("Message Sent!", {
      description: "We'll get back to you within 24 hours.",
    });
    setFormData({ name: '', email: '', company: '', service: 'general', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="relative py-32 bg-slate-50">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-slate-950 mb-6">
            {mockData.contact.title}
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {mockData.contact.subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-slate-200"
          >
            <h3 className="text-3xl font-bold text-slate-950 mb-6">Get in Touch</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                  className="w-full h-12 px-4 rounded-xl border-slate-300 focus:border-amber-400 focus:ring-amber-400 transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                  className="w-full h-12 px-4 rounded-xl border-slate-300 focus:border-amber-400 focus:ring-amber-400 transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Company</label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Your Company"
                  className="w-full h-12 px-4 rounded-xl border-slate-300 focus:border-amber-400 focus:ring-amber-400 transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Service Interest</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-amber-400 focus:ring-2 focus:ring-amber-400 bg-white transition-all duration-300"
                >
                  <option value="general">General Inquiry</option>
                  <option value="design">Design</option>
                  <option value="integration">Integration</option>
                  <option value="operations">Operations</option>
                  <option value="engineering">Engineering</option>
                  <option value="risk-management">Risk Management</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Message *</label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about your security needs..."
                  className="w-full px-4 py-3 rounded-xl border-slate-300 focus:border-amber-400 focus:ring-amber-400 transition-all duration-300 resize-none"
                />
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30 transition-all duration-300"
                >
                  <span>Send Message</span>
                  <Send className="w-4 h-4" />
                </Button>
              </motion.div>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-8"
          >
            {/* Info Cards */}
            <div className="space-y-6">
              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg border border-slate-200 group hover:border-amber-400 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-6 h-6 text-slate-950" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-950 mb-1">Email</h4>
                  <a href={`mailto:${mockData.contact.email}`} className="text-amber-600 hover:text-amber-700 transition-colors duration-300">
                    {mockData.contact.email}
                  </a>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg border border-slate-200 group hover:border-amber-400 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-6 h-6 text-slate-950" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-950 mb-1">Phone</h4>
                  <a href={`tel:${mockData.contact.phone}`} className="text-amber-600 hover:text-amber-700 transition-colors duration-300">
                    {mockData.contact.phone}
                  </a>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-lg border border-slate-200 group hover:border-amber-400 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-6 h-6 text-slate-950" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-950 mb-1">Location</h4>
                  <p className="text-slate-600">{mockData.contact.location}</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
